import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Home, Users, CheckCircle, AlertCircle, Loader } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import '../styles/VillaForm.css';

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, ease: "easeOut" }
};

const VillaForm = ({ isOpen = false, onClose = () => {} }) => {
  const [formData, setFormData] = useState({
    villaName: '',
    capacity: '',
    location: '',
    address: '',
    contactPerson: '',
    phone: '',
    email: '',
    preferredDate: '',
    preferredTimeSlot: '',
    description: '',
    rgpdAccepted: false
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCheckboxChange = (e) => {
    setFormData(prev => ({
      ...prev,
      rgpdAccepted: e.target.checked
    }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateForm = () => {
    if (!formData.villaName.trim()) return 'Veuillez entrer le nom de la villa';
    if (!formData.capacity) return 'Veuillez sélectionner une capacité';
    if (!formData.location) return 'Veuillez sélectionner une situation géographique';
    if (!formData.address.trim()) return 'Veuillez entrer l\'adresse complète';
    if (!formData.contactPerson.trim()) return 'Veuillez entrer le nom du contact';
    if (!formData.phone.trim()) return 'Veuillez entrer un numéro de téléphone';
    if (!formData.email.trim()) return 'Veuillez entrer une adresse email';
    if (!formData.email.includes('@')) return 'Veuillez entrer une adresse email valide';
    if (!formData.preferredDate) return 'Veuillez sélectionner une date';
    if (!formData.preferredTimeSlot) return 'Veuillez sélectionner une plage horaire';
    if (!formData.rgpdAccepted) return 'Veuillez accepter la politique de confidentialité';
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const error = validateForm();
    if (error) {
      setSubmitStatus({ type: 'error', message: error });
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Simulating API call - replace with your actual API endpoint
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      console.log('Form Data:', formData);
      setSubmitStatus({ 
        type: 'success', 
        message: 'Villa enregistrée avec succès ! Nous vous contacterons bientôt.' 
      });
      
      // Reset form after successful submission
      setTimeout(() => {
        setFormData({
          villaName: '',
          capacity: '',
          location: '',
          address: '',
          contactPerson: '',
          phone: '',
          email: '',
          preferredDate: '',
          preferredTimeSlot: '',
          description: '',
          rgpdAccepted: false
        });
        setSubmitStatus(null);
      }, 2000);
    } catch (error) {
      setSubmitStatus({ 
        type: 'error', 
        message: 'Une erreur est survenue. Veuillez réessayer.' 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {isOpen && (
        <div className="villa-form-modal-overlay" onClick={onClose}>
          <motion.div 
            className="villa-form-modal"
            onClick={(e) => e.stopPropagation()}
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.3 }}
            style={{ pointerEvents: 'auto' }}
          >
            <button 
              className="villa-form-modal-close" 
              onClick={onClose}
              aria-label="Fermer le formulaire"
            >
              ✕
            </button>

            <motion.section 
              className="villa-form-section-modal"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
            >
      <div className="villa-form-container">
        <motion.div 
          className="form-header"
          variants={fadeInUp}
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
        >
          <h2>Enregistrez votre Villa</h2>
          <p>Connectez votre propriété avec nos clients potentiels</p>
        </motion.div>

        <motion.form 
          onSubmit={handleSubmit}
          className="villa-form"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          {/* Status Message */}
          {submitStatus && (
            <motion.div 
              className={`status-message ${submitStatus.type}`}
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
            >
              {submitStatus.type === 'success' ? (
                <CheckCircle size={20} />
              ) : (
                <AlertCircle size={20} />
              )}
              <span>{submitStatus.message}</span>
            </motion.div>
          )}

          <div className="form-grid">
            {/* Villa Name */}
            <motion.div className="form-group" variants={fadeInUp}>
              <Label htmlFor="villaName">
                <Home size={18} />
                Nom de la Villa
              </Label>
              <Input
                id="villaName"
                name="villaName"
                type="text"
                placeholder="Ex: Villa Côte d'Azur"
                value={formData.villaName}
                onChange={handleInputChange}
                className="input-villa"
              />
            </motion.div>

            {/* Capacity */}
            <motion.div className="form-group" variants={fadeInUp}>
              <Label htmlFor="capacity">
                <Users size={18} />
                Capacité d'Accueil
              </Label>
              <Select value={formData.capacity} onValueChange={(value) => handleSelectChange('capacity', value)}>
                <SelectTrigger className="select-villa">
                  <SelectValue placeholder="Sélectionner une capacité" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="2-4">2-4 personnes</SelectItem>
                  <SelectItem value="4-6">4-6 personnes</SelectItem>
                  <SelectItem value="6-8">6-8 personnes</SelectItem>
                  <SelectItem value="8-10">8-10 personnes</SelectItem>
                  <SelectItem value="10-15">10-15 personnes</SelectItem>
                  <SelectItem value="15+">15+ personnes</SelectItem>
                </SelectContent>
              </Select>
            </motion.div>

            {/* Location */}
            <motion.div className="form-group" variants={fadeInUp}>
              <Label htmlFor="location">
                <MapPin size={18} />
                Situation Géographique
              </Label>
              <Select value={formData.location} onValueChange={(value) => handleSelectChange('location', value)}>
                <SelectTrigger className="select-villa">
                  <SelectValue placeholder="Sélectionner une région" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="reunion">Ile de la Réunion</SelectItem>
                  <SelectItem value="maurice">Ile Maurice</SelectItem>
                  <SelectItem value="france">France métropolitaine</SelectItem>
                  <SelectItem value="canada">Canada</SelectItem>
                  <SelectItem value="suisse">Suisse</SelectItem>
                  <SelectItem value="allemagne">Allemagne</SelectItem>
                  <SelectItem value="belgique">Belgique</SelectItem>
                </SelectContent>
              </Select>
            </motion.div>

            {/* Address */}
            <motion.div className="form-group" variants={fadeInUp}>
              <Label htmlFor="address">Adresse Complète</Label>
              <Input
                id="address"
                name="address"
                type="text"
                placeholder="Rue, code postal, ville"
                value={formData.address}
                onChange={handleInputChange}
                className="input-villa"
              />
            </motion.div>

            {/* Contact Person */}
            <motion.div className="form-group" variants={fadeInUp}>
              <Label htmlFor="contactPerson">Nom de la Personne à Contacter</Label>
              <Input
                id="contactPerson"
                name="contactPerson"
                type="text"
                placeholder="Votre nom complet"
                value={formData.contactPerson}
                onChange={handleInputChange}
                className="input-villa"
              />
            </motion.div>

            {/* Phone */}
            <motion.div className="form-group" variants={fadeInUp}>
              <Label htmlFor="phone">
                <Phone size={18} />
                Numéro de Téléphone
              </Label>
              <Input
                id="phone"
                name="phone"
                type="tel"
                placeholder="+33 6 XX XX XX XX"
                value={formData.phone}
                onChange={handleInputChange}
                className="input-villa"
              />
            </motion.div>

            {/* Email */}
            <motion.div className="form-group" variants={fadeInUp}>
              <Label htmlFor="email">
                <Mail size={18} />
                Adresse Email
              </Label>
              <Input
                id="email"
                name="email"
                type="email"
                placeholder="votreemail@exemple.com"
                value={formData.email}
                onChange={handleInputChange}
                className="input-villa"
              />
            </motion.div>

            {/* Description */}
            <motion.div className="form-group form-group-full" variants={fadeInUp}>
              <Label htmlFor="description">Description de la Villa (Optionnel)</Label>
              <Textarea
                id="description"
                name="description"
                placeholder="Décrivez votre villa, ses équipements, ses points forts..."
                value={formData.description}
                onChange={handleInputChange}
                className="textarea-villa"
              />
            </motion.div>
          </div>

          {/* Contact Preferences */}
          <motion.div className="form-preferences" variants={fadeInUp}>
            <h3>Quand souhaitez-vous être contacté ?</h3>
            <div className="contact-schedule-grid">
              <div className="form-group">
                <Label htmlFor="preferredDate">Date souhaitée</Label>
                <Input
                  id="preferredDate"
                  name="preferredDate"
                  type="date"
                  value={formData.preferredDate}
                  onChange={handleInputChange}
                  className="input-villa"
                />
              </div>
              <div className="form-group">
                <Label htmlFor="preferredTimeSlot">Plage horaire</Label>
                <Select value={formData.preferredTimeSlot} onValueChange={(value) => handleSelectChange('preferredTimeSlot', value)}>
                  <SelectTrigger className="select-villa">
                    <SelectValue placeholder="Sélectionner une plage" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="08-10">08:00 - 10:00</SelectItem>
                    <SelectItem value="10-12">10:00 - 12:00</SelectItem>
                    <SelectItem value="12-14">12:00 - 14:00</SelectItem>
                    <SelectItem value="14-16">14:00 - 16:00</SelectItem>
                    <SelectItem value="16-18">16:00 - 18:00</SelectItem>
                    <SelectItem value="18-20">18:00 - 20:00</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </motion.div>

          {/* RGPD Checkbox */}
          <motion.div className="form-rgpd" variants={fadeInUp}>
            <label className="rgpd-checkbox">
              <input
                type="checkbox"
                checked={formData.rgpdAccepted}
                onChange={handleCheckboxChange}
              />
              <span>
                J'accepte la{' '}
                <a
                  href="/mentions-legales-confidentialite/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="rgpd-link"
                >
                  politique de confidentialité de Séjour de rêve
                </a>
              </span>
            </label>
          </motion.div>

          {/* Submit Button */}
          <motion.div 
            className="form-submit"
            variants={fadeInUp}
          >
            <Button 
              type="submit"
              disabled={isSubmitting}
              className="btn-submit-villa"
            >
              {isSubmitting ? (
                <>
                  <Loader size={18} className="loader-icon" />
                  <span>Envoi en cours...</span>
                </>
              ) : (
                <span>Échanger au sujet de ma villa</span>
              )}
            </Button>
            <p className="form-note">
              Nous examinons votre demande et vous recontactons dans un délai de 24h ouvrées 
            </p>
            <p className="form-note-small"> 
              (hors dimanches et jours fériés).
            </p>
          </motion.div>
        </motion.form>
      </div>
            </motion.section>
          </motion.div>
        </div>
      )}
    </>
  );
};

export default VillaForm;
